import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Register.css';
import { SUPABASE_READY, supabase } from '../lib/supabaseClient';

const CATEGORIES = [
  'Most Unique Catch',
  'Smallest Cutest Catch',
  'Largest Fish by Length',
  'Most Species Caught',
  'Ugliest Fish',
];

const SPECIES = [
  'Bass',
  'Crappie',
  'Muskie',
  'Northern Pike',
  'Perch',
  'Sauger',
  'Trout',
  'Walleye',
  'Whitefish',
  'Other',
];

const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

function normalizeWhitespace(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function toTitleCase(value) {
  return value
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function normalizeSpecies(speciesValue) {
  const cleaned = normalizeWhitespace(speciesValue);
  if (!cleaned) return '';

  const match = SPECIES.find((item) => item.toLowerCase() === cleaned.toLowerCase());
  return match || toTitleCase(cleaned);
}

function fileExt(file) {
  const parts = file.name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : 'jpg';
}

function SubmitCatch() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userCatches, setUserCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catchMsg, setCatchMsg] = useState('');

  const [catchForm, setCatchForm] = useState({
    category: CATEGORIES[0],
    species: SPECIES[0],
    length: '',
    description: '',
    photo: null,
  });

  const speciesProgress = useMemo(() => {
    const uniqueSpecies = new Set(
      userCatches.map((entry) => normalizeSpecies(entry.species).toLowerCase()).filter(Boolean)
    ).size;
    return { uniqueSpecies };
  }, [userCatches]);

  useEffect(() => {
    if (!SUPABASE_READY) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function init() {
      const sessionResponse = await supabase.auth.getSession();
      if (mounted) {
        setSession(sessionResponse?.data?.session || null);
        setLoading(false);
      }
    }

    init();

    const authStateResponse = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    const subscription = authStateResponse?.data?.subscription;

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!SUPABASE_READY || !session?.user) {
      setProfile(null);
      setUserCatches([]);
      return;
    }

    async function loadProfileAndCatches() {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();
      setProfile(data || null);

      const { data: catchesData } = await supabase
        .from('catches')
        .select('id, species')
        .eq('user_id', session.user.id);
      setUserCatches(catchesData || []);
    }

    loadProfileAndCatches();
  }, [session]);

  async function uploadCatchPhoto(userId, file) {
    const ext = fileExt(file);
    const path = `${userId}/catch-${Date.now()}.${ext}`;

    const { error } = await supabase.storage
      .from('catch-photos')
      .upload(path, file, { upsert: false });

    if (error) throw error;
    return path;
  }

  const onCatchChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo') {
      setCatchForm((prev) => ({ ...prev, photo: files?.[0] || null }));
      return;
    }
    setCatchForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmitCatch = async (e) => {
    e.preventDefault();
    setCatchMsg('');

    if (!SUPABASE_READY) {
      setCatchMsg('Supabase is not configured yet.');
      return;
    }

    if (!session?.user || !profile) {
      setCatchMsg('Sign in first before submitting a catch.');
      return;
    }

    const normalizedCategory = normalizeWhitespace(catchForm.category);
    const normalizedSpecies = normalizeSpecies(catchForm.species);
    const normalizedDescription = normalizeWhitespace(catchForm.description);
    const parsedLength = Number(catchForm.length);

    if (!normalizedCategory) {
      setCatchMsg('Please choose a category.');
      return;
    }

    if (!normalizedSpecies) {
      setCatchMsg('Please choose a species.');
      return;
    }

    if (!Number.isFinite(parsedLength) || parsedLength <= 0) {
      setCatchMsg('Please enter a valid fish length greater than 0 inches.');
      return;
    }

    if (!normalizedDescription) {
      setCatchMsg('Please add a short description for your catch.');
      return;
    }

    if (!catchForm.photo) {
      setCatchMsg('Please upload a catch photo.');
      return;
    }

    if (!ALLOWED_PHOTO_TYPES.includes(catchForm.photo.type)) {
      setCatchMsg('Please upload a JPG, PNG, WEBP, or HEIC image.');
      return;
    }

    if (catchForm.photo.size > MAX_PHOTO_SIZE_BYTES) {
      setCatchMsg('Photo is too large. Maximum file size is 10MB.');
      return;
    }

    try {
      const photoPath = await uploadCatchPhoto(session.user.id, catchForm.photo);

      const { error } = await supabase.from('catches').insert({
        user_id: session.user.id,
        category: normalizedCategory,
        species: normalizedSpecies,
        length_inches: parsedLength,
        description: normalizedDescription,
        photo_path: photoPath,
      });

      if (error) {
        setCatchMsg(error.message);
        return;
      }

      setUserCatches((prev) => [...prev, { species: normalizedSpecies }]);

      setCatchForm({
        category: CATEGORIES[0],
        species: SPECIES[0],
        length: '',
        description: '',
        photo: null,
      });
      setCatchMsg('Catch submitted successfully.');
    } catch (err) {
      setCatchMsg(err.message || 'Could not submit catch.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="card"><p>Loading account details...</p></div>
      </div>
    );
  }

  if (!SUPABASE_READY) {
    return (
      <div className="page-container">
        <div className="card error-card">
          <p>Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to run submission features.</p>
        </div>
      </div>
    );
  }

  if (!session?.user || !profile) {
    return (
      <div className="page-container">
        <h1 className="page-title">Submit Catch</h1>
        <div className="card">
          <p>You must be signed in before submitting catches.</p>
          <p><Link to="/register">Go to account sign in</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">Submit Catch</h1>
      <p className="page-subtitle">Signed in as {profile.display_name}. Upload your fish entry below.</p>

      <div className="card">
        <div className="active-profile">
          <ProfilePhoto profile={profile} />
          <div>
            <p className="profile-name">Signed in as {profile.display_name}</p>
            <p className="profile-username">@{profile.username}</p>
            <p className="profile-species-count">
              Species tracked this derby: {speciesProgress.uniqueSpecies}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Catch Details</h2>
        <p className="catch-help">
          For consistency and cleaner judging, use the species dropdown whenever possible.
        </p>

        <form onSubmit={onSubmitCatch} className="register-form" encType="multipart/form-data">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select id="category" name="category" value={catchForm.category} onChange={onCatchChange} required>
                {CATEGORIES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="species">Species *</label>
              <select id="species" name="species" value={catchForm.species} onChange={onCatchChange} required>
                {SPECIES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="length">Length (inches) *</label>
              <input
                id="length"
                name="length"
                type="number"
                min="0"
                step="0.1"
                value={catchForm.length}
                onChange={onCatchChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="catchPhoto">Fish Photo *</label>
              <input
                id="catchPhoto"
                name="photo"
                type="file"
                accept="image/*"
                onChange={onCatchChange}
                required
              />
              <p className="profile-species-count">Accepted: JPG/PNG/WEBP/HEIC, up to 10MB.</p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={catchForm.description}
              onChange={onCatchChange}
              placeholder="Where you caught it, what lure worked, and why it fits the category."
              required
            />
          </div>

          {catchMsg && <p className="form-note">{catchMsg}</p>}
          <button type="submit" className="btn btn-primary">Submit Catch</button>
        </form>
      </div>
    </div>
  );
}

function ProfilePhoto({ profile }) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    let canceled = false;

    async function loadPhoto() {
      if (!profile?.profile_photo_path || !supabase) return;
      const { data, error } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(profile.profile_photo_path, 3600);
      if (!canceled && !error) {
        setUrl(data.signedUrl);
      }
    }

    loadPhoto();
    return () => {
      canceled = true;
    };
  }, [profile]);

  if (!url) {
    return <div className="profile-avatar profile-avatar-fallback">👤</div>;
  }

  return <img src={url} alt={profile.display_name} className="profile-avatar" />;
}

export default SubmitCatch;
